import {
  Button,
  CascaderField,
  Col,
  ContentItem,
  ContentItems,
  ContentWrapper,
  DatePickerField,
  FieldLabel,
  Flex,
  Form,
  HintIcon,
  type OperationType,
  RemovableCard,
  Row,
  SelectField,
  Text,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { SelectMinistryField } from '@/features/master-table/components';
import { isBefore2025, isBefore2026 } from '@/features/project/_utils/project';
import {
  BusinessFormLabelModifier,
  CorporateFormLabelModifier,
  FundNecessityTypeLabelModifier,
  OperationFormLabelModifier,
  ReasonForNotSettingEndPeriodLabelModifier,
  businessForm,
  corporateForm as corporateFormType,
  fundNecessityType,
  operationForm,
  reasonForNotSettingEndPeriod,
} from '@/features/project/projects/_models/project';
import { UserSelectField } from '@/features/tenant-manager/user/_components/UserSelectField';
import { PlusOutlined } from '@ant-design/icons';
import { type FC, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useSelectFiledFormData } from '../../_hooks/project-form-handler';
import type { KsFormSchema } from '../../_model/project-form';
import { FiscalYearField } from '../fields/FiscalYearField';
import { LegalBasesArrayField } from '../fields/LegalBasesArrayField';
import { OtherResponsibleOrganizationsArrayField } from '../fields/OtherResponsibleOrganizationsArrayField';
import { PeriodAndStartEndCheckField } from '../fields/PeriodAndStartEndCheckField';
import { PlansArrayField } from '../fields/PlansArrayField';
import { RelatedOrganizationArrayField } from '../fields/RelatedOrganizationArrayField';
import { SnapshotField } from '../fields/SnapshotField';
import { RelatedProjectArrayField } from '../fields/related-project-array-field/RelatedProjectArrayField';

type FormValues = z.infer<typeof KsFormSchema>;
type InitialFormValues = {
  data: Partial<FormValues['data']>;
};

type Props = {
  operation: OperationType;
  ignoredIdsForRelatedProject?: string[];
};

export const KsProjectForm: FC<Props> = ({
  operation,
  ignoredIdsForRelatedProject,
}) => {
  const {
    watch,
    control,
    resetField,
    formState: { errors },
    setValue,
  } = useFormContext<InitialFormValues, unknown, FormValues>();
  const { t } = useTranslation();

  const [fundSheetType, fiscalYear, corporateForm, fiscalYearOfSnapshot] =
    watch([
      'data.fundAdditionalAttributes.fundSheetType',
      'data.fiscalYear',
      'data.fundAdditionalAttributes.corporateForm',
      'data.fiscalYearOfSnapshot',
    ]);

  const snapshotId = fiscalYearOfSnapshot?.at(-1);

  const { organizationOptions } = useSelectFiledFormData({
    inputFiscalYear: fiscalYear,
    inputSnapshotId: snapshotId,
  });

  const isBefore2025Project = useMemo(
    () =>
      isBefore2025(fiscalYear ? { fiscalYear: fiscalYear?.year() } : undefined),
    [fiscalYear],
  );

  const isBefore2026Project = useMemo(
    () =>
      isBefore2026(fiscalYear ? { fiscalYear: fiscalYear?.year() } : undefined),
    [fiscalYear],
  );

  const {
    fields: relatedAdditionalJurisdictionalUsers,
    append: appendAdditionalJurisdictionalUsers,
    remove: removeAdditionalJurisdictionalUsers,
  } = useFieldArray({
    control,
    name: 'data.fundAdditionalAttributes.additionalJurisdictionalUsers',
  });

  const fundMethodNecessityFields = (
    <>
      <ContentItems
        items={[
          <SelectField
            key="data.fundAdditionalAttributes.necessityType"
            name="data.fundAdditionalAttributes.necessityType"
            label={t('fund-necessity-type')}
            control={control}
            errors={errors}
            options={fundNecessityType.options.map((value) => ({
              value,
              label: FundNecessityTypeLabelModifier[value],
            }))}
          />,
          <TextAreaInputField
            key="data.fundAdditionalAttributes.necessityReasons"
            name="data.fundAdditionalAttributes.necessityReasons"
            label={t('fund-necessity-reasons-for-local-government')}
            hint={t('hint-fund-necessity-reasons')}
            control={control}
            errors={errors}
            rows={3}
          />,
        ]}
      />
    </>
  );

  return (
    <>
      <ContentWrapper paddingTop={0}>
        <ContentItem borderBottom="0" paddingBottom={0} paddingTop={0}>
          <ContentItems
            mode="splitLeft"
            items={[
              <FiscalYearField
                key="data.fiscalYear"
                operation={operation}
                onChange={() => {
                  resetField('data.organizationIds', { defaultValue: [] });
                }}
              />,

              <SnapshotField key="data.snapshotId" />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('organization')}>
        <ContentItem>
          <ContentItems
            items={[
              <SelectMinistryField
                key="data.ministryId"
                name="data.ministryId"
                control={control}
                errors={errors}
              />,
              <CascaderField
                key="data.organizationIds"
                name="data.organizationIds"
                label={t('organization-in-charge')}
                hint={t('hint-organization-in-charge-for-ks')}
                control={control}
                errors={errors}
                options={organizationOptions.data}
                enableIntermediate
                disabled={!fiscalYear}
                required
                textNotInOption={t('not-exist-in-organization-options')}
                loading={organizationOptions.isPending}
              />,
            ]}
          />
          <ContentItems
            items={[
              <UserSelectField
                key="data.peopleInChargeIds"
                name="data.peopleInChargeIds"
                label={t('people-in-charge')}
                hint={t('hint-people-in-charge-for-ks')}
                control={control}
                errors={errors}
                mode="multiple"
                organizationFilter="modal"
                useNotInOption
                snapshotId={snapshotId ?? undefined}
                required
              />,
              <UserSelectField
                key="data.businessManagerIds"
                name="data.businessManagerIds"
                label={t('business-manager')}
                hint={t('hint-business-manager-for-ks')}
                placeholder={t('placeholder-business-manager')}
                control={control}
                errors={errors}
                mode="multiple"
                organizationFilter="modal"
                useNotInOption
                snapshotId={snapshotId ?? undefined}
                required
              />,
            ]}
          />

          {fundSheetType === 'standard' && (
            <Form.Item
              label={
                <FieldLabel
                  text={t('additional-organization-in-charge')}
                  hint={t('hint-additional-organization-in-charge-for-ks')}
                />
              }
              key="additional-organization-in-charge"
            >
              <Row gutter={[24, 8]}>
                {relatedAdditionalJurisdictionalUsers.map((item, idx) => {
                  return (
                    <Col key={item.id} span={24}>
                      <RemovableCard
                        onRemove={() => {
                          removeAdditionalJurisdictionalUsers(idx);
                        }}
                      >
                        <Row gutter={[24, 8]} wrap>
                          <Col span={12}>
                            <SelectMinistryField
                              name={`data.fundAdditionalAttributes.additionalJurisdictionalUsers.${idx}.ministryId`}
                              control={control}
                              errors={errors}
                            />
                          </Col>
                          <Col span={12}>
                            <CascaderField
                              name={`data.fundAdditionalAttributes.additionalJurisdictionalUsers.${idx}.organizationIds`}
                              label={t('organization-in-charge')}
                              control={control}
                              errors={errors}
                              options={organizationOptions.data}
                              enableIntermediate
                              required
                              loading={organizationOptions.isPending}
                              textNotInOption={t(
                                'not-exist-in-organization-options',
                              )}
                            />
                          </Col>

                          <Col span={12}>
                            <UserSelectField
                              name={`data.fundAdditionalAttributes.additionalJurisdictionalUsers.${idx}.peopleInChargeIds`}
                              label={t('people-in-charge')}
                              control={control}
                              errors={errors}
                              mode="multiple"
                              organizationFilter="modal"
                              useNotInOption
                              snapshotId={snapshotId ?? undefined}
                              required
                            />
                          </Col>
                          <Col span={12}>
                            <UserSelectField
                              mode="multiple"
                              name={`data.fundAdditionalAttributes.additionalJurisdictionalUsers.${idx}.businessManagerIds`}
                              label={t('business-manager')}
                              control={control}
                              errors={errors}
                              organizationFilter="modal"
                              useNotInOption
                              snapshotId={snapshotId ?? undefined}
                              required
                            />
                          </Col>
                        </Row>
                      </RemovableCard>
                    </Col>
                  );
                })}
                <Col span={12}>
                  <Button
                    type="dashed"
                    onClick={() => {
                      appendAdditionalJurisdictionalUsers([
                        {
                          ministryId: '',
                          organizationIds: [],
                          peopleInChargeIds: [],
                          businessManagerIds: [],
                        },
                      ]);
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    {t('add-organization-in-charge')}
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          )}
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <OtherResponsibleOrganizationsArrayField type="KS" />
          <RelatedOrganizationArrayField type="KS" required />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('basic-information')}>
        <ContentItem
          {...(fundSheetType === 'investment' && {
            borderBottom: 0,
          })}
        >
          <ContentItems
            items={[
              <TextInputField
                key="data.name"
                name="data.name"
                autocompleteKey="ksProject.name"
                label={t('fund-name')}
                control={control}
                errors={errors}
                required
              />,
              fundSheetType === 'investment' && (
                <TextInputField
                  name="data.fundAdditionalAttributes.fundOwner"
                  label={t('corporate-name')}
                  control={control}
                  errors={errors}
                />
              ),
            ]}
          />

          {(fundSheetType === 'standard' ||
            fundSheetType === 'local-government') && (
            <ContentItems
              items={[
                <TextInputField
                  key="data.fundAdditionalAttributes.fundBusinessName"
                  name="data.fundAdditionalAttributes.fundBusinessName"
                  label={t('fund-business-name')}
                  control={control}
                  errors={errors}
                />,
                fundSheetType === 'standard' && (
                  <TextInputField
                    name="data.fundAdditionalAttributes.fundOwner"
                    label={t('fund-owner')}
                    control={control}
                    errors={errors}
                  />
                ),
              ]}
            />
          )}

          <ContentItems
            mode="fillRight"
            items={[
              fundSheetType === 'standard' ? (
                <SelectField
                  key="data.fundAdditionalAttributes.corporateForm"
                  name="data.fundAdditionalAttributes.corporateForm"
                  label={t('corporate-form')}
                  control={control}
                  errors={errors}
                  options={corporateFormType.options.map((value) => ({
                    value,
                    label: CorporateFormLabelModifier[value],
                  }))}
                  shouldCoexistOnChange
                  onChange={(value) => {
                    if (value !== 'others') {
                      setValue(
                        'data.fundAdditionalAttributes.optionalCorporateForm',
                        null,
                        { shouldDirty: true },
                      );
                    }
                  }}
                />
              ) : null,
              corporateForm === 'others' ? (
                <TextInputField
                  key="data.fundAdditionalAttributes.optionalCorporateForm"
                  name="data.fundAdditionalAttributes.optionalCorporateForm"
                  label={t('optional-corporate-form')}
                  control={control}
                  errors={errors}
                />
              ) : null,
            ]}
          />

          {fundSheetType === 'standard' && (
            <ContentItems
              items={[
                <TextAreaInputField
                  key="data.inspectionEvaluation.eligibilityInspection.methodAndReasonsForSelection"
                  name="data.inspectionEvaluation.eligibilityInspection.methodAndReasonsForSelection"
                  label={
                    isBefore2025Project
                      ? t(
                          'eligibility-inspection-method-and-reasons-for-selection-before-2025',
                        )
                      : t(
                          'eligibility-inspection-method-and-reasons-for-selection',
                        )
                  }
                  control={control}
                  errors={errors}
                  rows={2}
                />,
                <TextAreaInputField
                  key="data.inspectionEvaluation.eligibilityInspection.results"
                  name="data.inspectionEvaluation.eligibilityInspection.results"
                  label={
                    isBefore2025Project
                      ? t('eligibility-inspection-results-before-2025')
                      : t('eligibility-inspection-results')
                  }
                  control={control}
                  errors={errors}
                  rows={2}
                />,
              ]}
            />
          )}

          {fundSheetType === 'standard' || fundSheetType === 'investment' ? (
            <ContentItems
              mode="fullLine"
              items={[
                fundSheetType === 'standard' ? (
                  <TextAreaInputField
                    key="data.inspectionEvaluation.implementationStatusOfInspection"
                    name="data.inspectionEvaluation.implementationStatusOfInspection"
                    label={t('implementation-status-of-inspection')}
                    control={control}
                    errors={errors}
                    rows={2}
                  />
                ) : null,
                fundSheetType === 'investment' || isBefore2025Project ? (
                  <PeriodAndStartEndCheckField
                    key="data.period-data.isUnknownStart-data.isNoScheduledEnd"
                    hint={t('hint-project-period')}
                  />
                ) : null,
              ]}
            />
          ) : null}
        </ContentItem>

        {(fundSheetType === 'standard' ||
          fundSheetType === 'local-government') && (
          <ContentItem borderBottom={0} paddingBottom={0}>
            <ContentItems
              items={[
                <SelectField
                  key="data.fundAdditionalAttributes.operationForms"
                  name="data.fundAdditionalAttributes.operationForms"
                  label={t('operation-form')}
                  control={control}
                  errors={errors}
                  options={operationForm.options.map((value) => ({
                    value,
                    label: OperationFormLabelModifier[value],
                  }))}
                  mode="multiple"
                />,
                <SelectField
                  key="data.fundAdditionalAttributes.businessForms"
                  name="data.fundAdditionalAttributes.businessForms"
                  label={t('business-form')}
                  control={control}
                  errors={errors}
                  options={businessForm.options.map((value) => ({
                    value,
                    label: BusinessFormLabelModifier[value],
                  }))}
                  mode="multiple"
                />,
              ]}
            />
            {fundSheetType === 'standard' && (
              <RelatedProjectArrayField
                operation={operation}
                type="KS"
                ignoredProjectIds={ignoredIdsForRelatedProject}
              />
            )}
          </ContentItem>
        )}
      </ContentWrapper>

      {fundSheetType === 'local-government' && (
        <>
          <ContentWrapper title={t('overview-and-purpose')}>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <ContentItems
                items={[
                  <TextAreaInputField
                    key="data.overview"
                    name="data.overview"
                    label={t('project-overview')}
                    hint={t('hint-project-overview-for-ks')}
                    control={control}
                    errors={errors}
                    rows={3}
                  />,
                ]}
              />
            </ContentItem>
          </ContentWrapper>

          <ContentWrapper title={t('necessity-of-fund-method')}>
            <ContentItem borderBottom={0} paddingBottom={0}>
              {fundMethodNecessityFields}
            </ContentItem>
          </ContentWrapper>

          <ContentWrapper title={t('scheduled-end-time')}>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <ContentItems
                items={[
                  <DatePickerField
                    key="fund-scheduled-end-period-for-local-government"
                    name="data.fundAdditionalAttributes.scheduledEnd.endDate"
                    label={t('fund-scheduled-end-period-for-local-government')}
                    hint={t('hint-fund-scheduled-end-period')}
                    control={control}
                    errors={errors}
                    picker="date"
                  />,
                  <DatePickerField
                    key="end-date-of-new-application-for-local-government"
                    name="data.fundAdditionalAttributes.scheduledEnd.endDateOfNewApplicationReceiving"
                    label={t(
                      'end-date-of-new-application-for-local-government',
                    )}
                    hint={t('hint-end-date-of-new-application')}
                    control={control}
                    errors={errors}
                    picker="date"
                  />,
                ]}
              />
            </ContentItem>
          </ContentWrapper>
        </>
      )}

      {(fundSheetType === 'standard' || fundSheetType === 'investment') && (
        <ContentWrapper title={t('overview-and-purpose')}>
          <ContentItem borderBottom={0} paddingBottom={0}>
            {fundSheetType === 'standard' && (
              <ContentItems
                items={[
                  <TextAreaInputField
                    key="data.purpose"
                    name="data.purpose"
                    label={t('project-purpose')}
                    hint={t('hint-project-purpose-for-ks')}
                    control={control}
                    errors={errors}
                    rows={3}
                  />,

                  <TextAreaInputField
                    key="data.task"
                    name="data.task"
                    label={t('project-task')}
                    hint={t('hint-project-task-for-ks')}
                    control={control}
                    errors={errors}
                    rows={3}
                  />,
                ]}
              />
            )}

            <ContentItems
              items={[
                <TextAreaInputField
                  key="data.overview"
                  name="data.overview"
                  label={t('project-overview')}
                  hint={t('hint-project-overview-for-ks')}
                  control={control}
                  errors={errors}
                  rows={3}
                />,
                fundSheetType === 'standard' && (
                  <TextInputField
                    key="data.overviewUrl"
                    name="data.overviewUrl"
                    label={t('overview-url')}
                    hint={t('hint-overview-url-for-ks')}
                    control={control}
                    errors={errors}
                  />
                ),
              ]}
            />
            {fundSheetType === 'standard' &&
              fiscalYear &&
              isBefore2025Project && (
                <ContentItems
                  items={[
                    <TextAreaInputField
                      key="data.fundAdditionalAttributes.effortAndResult"
                      name="data.fundAdditionalAttributes.effortAndResult"
                      label={t('effort-and-result')}
                      control={control}
                      errors={errors}
                    />,
                  ]}
                />
              )}
          </ContentItem>
        </ContentWrapper>
      )}

      {fundSheetType === 'standard' && (
        <>
          <ContentWrapper title={t('necessity-of-fund-method')}>
            <ContentItem borderBottom={0} paddingBottom={0}>
              {fundMethodNecessityFields}
            </ContentItem>
          </ContentWrapper>
          {!isBefore2025Project && (
            <ContentWrapper title={t('start-fiscal-year-of-project')}>
              <ContentItem borderBottom={0} paddingBottom={0}>
                <PeriodAndStartEndCheckField
                  hint={t('hint-project-period-for-ks')}
                  isStartOnly
                />
              </ContentItem>
            </ContentWrapper>
          )}
          <ContentWrapper title={t('scheduled-end-time')}>
            <ContentItem>
              <ContentItems
                items={[
                  <DatePickerField
                    key="fund-scheduled-end-period"
                    name="data.fundAdditionalAttributes.scheduledEnd.endDate"
                    label={t('fund-scheduled-end-period')}
                    hint={t('hint-fund-scheduled-end-period')}
                    control={control}
                    errors={errors}
                    picker="date"
                  />,
                  isBefore2025Project ? (
                    <SelectField
                      key="reason-for-not-setting-scheduled-end-period"
                      name="data.fundAdditionalAttributes.scheduledEnd.reasonsForNotSettingScheduledEndPeriod"
                      label={t('reason-for-not-setting-scheduled-end-period')}
                      control={control}
                      errors={errors}
                      options={reasonForNotSettingEndPeriod.options.map(
                        (value) => ({
                          value,
                          label:
                            ReasonForNotSettingEndPeriodLabelModifier[value],
                        }),
                      )}
                    />
                  ) : null,
                ]}
              />
              <ContentItems
                items={[
                  <TextAreaInputField
                    key="supplementary-reasons"
                    name="data.fundAdditionalAttributes.scheduledEnd.supplementaryReasonsForEndPeriod"
                    label={t('supplementary-reasons')}
                    control={control}
                    errors={errors}
                    rows={2}
                  />,

                  <TextAreaInputField
                    key="reason-for-changing-scheduled-end-period"
                    name="data.fundAdditionalAttributes.scheduledEnd.reasonsForChangingScheduledEndPeriod"
                    label={t('reason-for-changing-scheduled-end-period')}
                    control={control}
                    errors={errors}
                    rows={2}
                  />,
                ]}
              />
            </ContentItem>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <ContentItems
                items={[
                  <DatePickerField
                    key="end-date-of-new-application"
                    name="data.fundAdditionalAttributes.scheduledEnd.endDateOfNewApplicationReceiving"
                    label={t('end-date-of-new-application')}
                    hint={t('hint-end-date-of-new-application')}
                    control={control}
                    errors={errors}
                    picker="date"
                  />,
                  isBefore2025Project ? (
                    <SelectField
                      key="reason-for-not-setting-end-date-of-new-application"
                      name="data.fundAdditionalAttributes.scheduledEnd.reasonsForNotSettingEndPeriodOfNewApplicationReceiving"
                      label={t(
                        'reason-for-not-setting-end-date-of-new-application',
                      )}
                      control={control}
                      errors={errors}
                      options={reasonForNotSettingEndPeriod.options.map(
                        (value) => ({
                          value,
                          label:
                            ReasonForNotSettingEndPeriodLabelModifier[value],
                        }),
                      )}
                    />
                  ) : null,
                ]}
              />
              <ContentItems
                items={[
                  <TextAreaInputField
                    key="supplementary-reasons"
                    name="data.fundAdditionalAttributes.scheduledEnd.supplementaryReasonsForNewApplicationReceiving"
                    label={t('supplementary-reasons')}
                    control={control}
                    errors={errors}
                    rows={2}
                  />,

                  <TextAreaInputField
                    key="reason-for-changing-end-date-of-new-application"
                    name="data.fundAdditionalAttributes.scheduledEnd.reasonsForChangingEndPeriodOfNewApplicationReceiving"
                    label={t('reason-for-changing-end-date-of-new-application')}
                    control={control}
                    errors={errors}
                    rows={2}
                  />,
                ]}
              />
            </ContentItem>
          </ContentWrapper>
        </>
      )}

      {fundSheetType === 'standard' && (
        <>
          <ContentWrapper title={t('legal-bases')}>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <LegalBasesArrayField />
            </ContentItem>
          </ContentWrapper>

          <ContentWrapper
            title={
              <Flex align="center" gap={4}>
                <Text variant="h3" text={t('project-plan')} />
                <HintIcon hint={t('hint-plan-content')} />
              </Flex>
            }
          >
            <ContentItem borderBottom={0} paddingBottom={0}>
              <Form.Item>
                <PlansArrayField
                  urlHint={t('hint-plan-url')}
                  autocompleteKey="ksProject.plan.url"
                  gutter={[24, 0]}
                />
              </Form.Item>
            </ContentItem>
          </ContentWrapper>
        </>
      )}

      <ContentWrapper title={t('others')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          {fundSheetType === 'local-government' && (
            <ContentItems
              items={[
                <TextAreaInputField
                  key="data.task"
                  name="data.task"
                  label={t('status-of-response-by-ministries')}
                  control={control}
                  errors={errors}
                  rows={3}
                />,
              ]}
            />
          )}

          <ContentItems
            items={[
              <TextAreaInputField
                key="data.remark"
                name="data.remark"
                label={t('remark')}
                hint={isBefore2026Project ? undefined : t('hint-ks-remark')}
                control={control}
                errors={errors}
                rows={3}
              />,
              <TextInputField
                key="data.oldProjectNumber"
                name="data.oldProjectNumber"
                label={t('old-project-number')}
                control={control}
                errors={errors}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
